import React, { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate } from "react-router-dom";
import { Toaster } from 'sonner';

// Components
import { Navbar } from "./components/Navbar";
import { HeroSection } from "./components/HeroSection";
import { EcosystemSection } from "./components/EcosystemSection";
import { HowItWorksSection } from "./components/HowItWorksSection";
import { LivePlatformSection } from "./components/LivePlatformSection";
import { ProductStackSection } from "./components/ProductStackSection";
import { AudienceSection } from "./components/AudienceSection";
import { TestimonialsSection } from "./components/TestimonialsSection";
import { DisruptionSection } from "./components/DisruptionSection";
import { ExpansionMapSection } from "./components/ExpansionMapSection";
import { FinalCTASection } from "./components/FinalCTASection";
import { ContactForm } from "./components/ContactForm";
import { Footer } from "./components/Footer";
import { ChatBot } from "./components/ChatBot";
import { DataCrawlerCanvas } from "./components/DataCrawlerCanvas";
import { PartnerApplicationForm } from "./components/PartnerApplicationForm";
import { AdminCRM } from "./components/AdminCRM";
import WorkOrderRespond from "./components/WorkOrderRespond";
import { PartnerLogosSection } from "./components/PartnerLogosSection";
import { PublicScheduler } from "./components/PublicScheduler";
import { AnimationDemo } from "./components/AnimationDemo";
import { PortalSelector } from "./components/PortalSelector";

// Product Pages
import { InsurancePage } from "./components/InsurancePage";
import { PaymentsPage } from "./components/PaymentsPage";
import { DispatchPage } from "./components/DispatchPage";
import { CamerasPage } from "./components/CamerasPage";
import { CredentialingPage } from "./components/CredentialingPage";

// OVA Xchange
import { OVAXchangeLanding } from "./components/OVAXchangeLanding";

// Legal & Utility Pages
import { PrivacyPolicyPage } from "./components/PrivacyPolicyPage";
import { TermsOfServicePage } from "./components/TermsOfServicePage";
import { NotFoundPage } from "./components/NotFoundPage";
import { InsuranceDetailsPage } from "./components/InsuranceDetailsPage";
import { PaymentSuccess, PaymentCancel } from "./components/PaymentPages";

// Credential Exchange Portal
import { ExchangeAuthProvider } from "./contexts/ExchangeAuthContext";
import { PortalLogin } from "./components/portal/PortalLogin";
import { DriverDashboard } from "./components/portal/DriverDashboard";
import { ProviderDashboard } from "./components/portal/ProviderDashboard";
import { BrokerDashboard } from "./components/portal/BrokerDashboard";
import { PatientDashboard } from "./components/portal/PatientDashboard";
import { DriverCredentialsPage, ProviderCredentialsPage, BrokerCredentialsPage } from "./components/portal/CredentialsPage";
import { DriverTrustPage, ProviderTrustPage, BrokerTrustPage } from "./components/portal/TrustPage";
import { ComplianceDashboard } from "./components/portal/ComplianceDashboard";
import { ProviderCompliancePage, BrokerCompliancePage } from "./components/portal/CompliancePage";
import { DriverPassportPage } from "./components/portal/DriverPassportPage";
import { PortalLayout } from "./components/portal/PortalLayout";

// Public Passport Verification
import { PassportVerification } from "./components/PassportVerification";

// Contract Signing Page
import { ContractSigningPage } from "./components/ContractSigningPage";

// Insurance Landing Page (Founding Provider Funnel)
import { InsuranceLandingPage } from "./components/insurance/InsuranceLandingPage";

// Wrapper for driver passport page
const DriverPassportPageWrapper = () => (
  <PortalLayout role="driver">
    <DriverPassportPage />
  </PortalLayout>
);

// Register service worker for push notifications
const registerServiceWorker = async () => {
  if ('serviceWorker' in navigator) {
    try {
      const registration = await navigator.serviceWorker.register('/sw.js');
      console.log('Service Worker registered:', registration.scope);
    } catch (error) {
      console.log('Service Worker registration failed:', error);
    }
  }
};

const HomePage = () => {
  const navigate = useNavigate();
  
  // Register service worker on mount
  useEffect(() => {
    registerServiceWorker();
  }, []);
  
  const handlePartnerClick = () => {
    navigate('/partner');
  };

  return (
    <div className="relative min-h-screen bg-[#0c1222]">
      {/* Neural Network Background */}
      <DataCrawlerCanvas />
      
      {/* Navigation */}
      <Navbar onPartnerClick={handlePartnerClick} />
      
      {/* Main Content */}
      <main className="relative z-10">
        <HeroSection onPartnerClick={handlePartnerClick} />
        <PartnerLogosSection />
        <EcosystemSection />
        <HowItWorksSection />
        <LivePlatformSection />
        <ProductStackSection />
        <AudienceSection />
        <TestimonialsSection />
        <DisruptionSection />
        <ExpansionMapSection />
        <FinalCTASection onPartnerClick={handlePartnerClick} />
        <ContactForm />
      </main>
      
      {/* Footer */}
      <Footer />
      
      {/* AI Chatbot */}
      <ChatBot />
      
      {/* Toast Notifications */}
      <Toaster 
        position="bottom-right"
        toastOptions={{
          style: {
            background: '#0f172a',
            border: '1px solid rgba(255,255,255,0.1)',
            color: '#f8fafc',
          },
        }}
      />
    </div>
  );
};

const PartnerPage = () => {
  const navigate = useNavigate();
  
  return (
    <div className="relative min-h-screen bg-[#0c1222]">
      <DataCrawlerCanvas />
      <div className="relative z-10 py-12 px-4">
        <PartnerApplicationForm onBack={() => navigate('/')} />
      </div>
      <Toaster 
        position="bottom-right"
        toastOptions={{
          style: {
            background: '#0f172a',
            border: '1px solid rgba(255,255,255,0.1)',
            color: '#f8fafc',
          },
        }}
      />
    </div>
  );
};

const AdminPage = () => {
  const navigate = useNavigate();
  
  return (
    <div className="relative min-h-screen bg-[#0c1222]">
      <DataCrawlerCanvas />
      <div className="relative z-10 py-8 px-4">
        <AdminCRM onBack={() => navigate('/')} />
      </div>
      <Toaster 
        position="top-center"
        toastOptions={{
          style: {
            background: '#0f172a',
            border: '1px solid rgba(255,255,255,0.1)',
            color: '#f8fafc',
          },
        }}
      />
    </div>
  );
};

// Reusable toast config
const toastConfig = {
  position: "bottom-right",
  toastOptions: {
    style: {
      background: '#0f172a',
      border: '1px solid rgba(255,255,255,0.1)',
      color: '#f8fafc',
    },
  }
};

// Product page wrapper with consistent styling
const ProductPageWrapper = ({ children }) => (
  <div className="relative min-h-screen bg-[#0c1222]">
    <DataCrawlerCanvas />
    <div className="relative z-10">
      {children}
    </div>
    <Toaster {...toastConfig} />
  </div>
);

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ExchangeAuthProvider>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/partner" element={<PartnerPage />} />
            <Route path="/admin" element={<AdminPage />} />
            <Route path="/wo/respond/:token" element={<><WorkOrderRespond /><Toaster {...toastConfig} /></>} />
            {/* Product Pages */}
            <Route path="/insurance" element={<ProductPageWrapper><InsurancePage /></ProductPageWrapper>} />
            <Route path="/insurance/details" element={<ProductPageWrapper><InsuranceDetailsPage /></ProductPageWrapper>} />
            <Route path="/insurance/founding-provider" element={<InsuranceLandingPage />} />
            <Route path="/payments" element={<ProductPageWrapper><PaymentsPage /></ProductPageWrapper>} />
            <Route path="/dispatch" element={<ProductPageWrapper><DispatchPage /></ProductPageWrapper>} />
            <Route path="/cameras" element={<ProductPageWrapper><CamerasPage /></ProductPageWrapper>} />
            <Route path="/credentialing" element={<ProductPageWrapper><CredentialingPage /></ProductPageWrapper>} />
            {/* OVA Xchange - Credential Exchange Platform */}
            <Route path="/xchange" element={<OVAXchangeLanding />} />
            <Route path="/xchange/login" element={<PortalLogin />} />
            {/* Portal Selector with dramatic entrances */}
            <Route path="/portals" element={<PortalSelector />} />
            {/* Legacy portal login route - redirect to xchange */}
            <Route path="/portal/login" element={<PortalLogin />} />
            {/* Driver Portal */}
            <Route path="/portal/driver" element={<DriverDashboard />} />
            <Route path="/portal/driver/credentials" element={<DriverCredentialsPage />} />
            <Route path="/portal/driver/passport" element={<DriverPassportPageWrapper />} />
            <Route path="/portal/driver/trust" element={<DriverTrustPage />} />
            <Route path="/portal/driver/*" element={<DriverDashboard />} />
            {/* Provider Portal */}
            <Route path="/portal/provider" element={<ProviderDashboard />} />
            <Route path="/portal/provider/compliance" element={<ProviderCompliancePage />} />
            <Route path="/portal/provider/credentials" element={<ProviderCredentialsPage />} />
            <Route path="/portal/provider/trust" element={<ProviderTrustPage />} />
            <Route path="/portal/provider/*" element={<ProviderDashboard />} />
            {/* Broker Portal */}
            <Route path="/portal/broker" element={<BrokerDashboard />} />
            <Route path="/portal/broker/compliance" element={<BrokerCompliancePage />} />
            <Route path="/portal/broker/credentials" element={<BrokerCredentialsPage />} />
            <Route path="/portal/broker/trust" element={<BrokerTrustPage />} />
            <Route path="/portal/broker/*" element={<BrokerDashboard />} />
            {/* Patient Portal */}
            <Route path="/portal/patient" element={<PatientDashboard />} />
            <Route path="/portal/patient/*" element={<PatientDashboard />} />
            {/* Payment Pages */}
            <Route path="/payment/success" element={<ProductPageWrapper><PaymentSuccess /></ProductPageWrapper>} />
            <Route path="/payment/cancel" element={<ProductPageWrapper><PaymentCancel /></ProductPageWrapper>} />
            {/* Public Scheduler */}
            <Route path="/schedule" element={<PublicScheduler />} />
            {/* Animation Demo */}
            <Route path="/animations" element={<AnimationDemo />} />
            {/* Public Passport Verification */}
            <Route path="/verify/:passportId" element={<PassportVerification />} />
            {/* Contract Signing */}
            <Route path="/sign/:token" element={<ContractSigningPage />} />
            {/* Legal Pages */}
            <Route path="/privacy-policy" element={<ProductPageWrapper><PrivacyPolicyPage /></ProductPageWrapper>} />
            <Route path="/terms-of-service" element={<ProductPageWrapper><TermsOfServicePage /></ProductPageWrapper>} />
            {/* 404 Page - Must be last */}
            <Route path="*" element={<ProductPageWrapper><NotFoundPage /></ProductPageWrapper>} />
          </Routes>
        </ExchangeAuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
